import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { PreviewDataGrid } from "./PreviewDataGrid";
import type { ParsedTable } from "./types";

export interface QuantityColumnDialogProps {
  open: boolean;
  label: string;
  invalidHelpText: string;
  dialogInput: string;
  table: ParsedTable;
  numericCandidates: number[];
  selectedQuantityCol: number | null;
  onChangeInput: (value: string) => void;
  onChangeSelectedCol: (value: number | null) => void;
  onCancel: () => void;
  onApply: () => void;
  disabledApply: boolean;
}

export const QuantityColumnDialog = ({
  open,
  label,
  invalidHelpText,
  dialogInput,
  table,
  numericCandidates,
  selectedQuantityCol,
  onChangeInput,
  onChangeSelectedCol,
  onCancel,
  onApply,
  disabledApply,
}: QuantityColumnDialogProps) => {
  const { t } = useTenantTranslation();

  const getHelperText = (): string => {
    if (dialogInput.length === 0) return "";
    if (table.rows.length > 0) {
      const dataRowCount = table.hasHeaderRow
        ? table.rows.length - 1
        : table.rows.length;
      const headerInfo = table.hasHeaderRow ? " (with headers)" : "";
      return `Detected ${dataRowCount} data rows${headerInfo}`;
    }
    return invalidHelpText;
  };

  const hasInputError = dialogInput.length > 0 && table.rows.length === 0;
  const showColumnSelector = table.numColumns > 1;

  const handleQuantityColChange = (value: string | number) => {
    const numericValue = typeof value === "number" ? value : Number(value);
    onChangeSelectedCol(numericValue);
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          placeholder={t("ctrlVToPaste")}
          value={dialogInput}
          onChange={(event) => {
            onChangeInput(event.target.value);
          }}
          helperText={getHelperText()}
          error={hasInputError}
        />
        {showColumnSelector ? (
          <>
            <FormControl sx={{ mt: 2, minWidth: 240 }} size="small">
              <InputLabel id="quantity-col-label">
                {t("quantityColumn")}
              </InputLabel>
              <Select
                labelId="quantity-col-label"
                label={t("quantityColumn")}
                value={selectedQuantityCol ?? ""}
                onChange={(event) => {
                  handleQuantityColChange(event.target.value);
                }}
              >
                {numericCandidates.length === 0 ? (
                  <MenuItem value="" disabled>
                    {t("noNumericColumnsDetected")}
                  </MenuItem>
                ) : (
                  numericCandidates.map((columnIndex) => {
                    const displayName =
                      table.headers?.[columnIndex] ??
                      `${t("column")} ${columnIndex + 1}`;

                    return (
                      <MenuItem key={`col-${columnIndex}`} value={columnIndex}>
                        {displayName}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>
            <PreviewDataGrid
              table={table}
              selectedQuantityCol={selectedQuantityCol}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t("cancel")}</Button>
        <Button variant="contained" disabled={disabledApply} onClick={onApply}>
          {t("apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuantityColumnDialog;
